import React, { useState } from 'react';
import axios from 'axios';
import { X, Send, Mail, Loader2, Check, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailShareModal({ contentId, contentTitle, onClose }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [shareUrl, setShareUrl] = useState('');

  const handleSend = async () => {
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }
    
    // Basic email validation
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setSending(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/share/email`, {
        recipient_email: email,
        recipient_name: name || undefined,
        subject: subject || undefined,
        message: message || undefined,
        content_id: contentId
      });
      
      setShareUrl(response.data.share_url);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="email-share-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-[#E2DFD8]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#1B3A6B] rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold text-[#1B3A6B]">Share via Email</h2>
                <p className="text-sm text-[#475569]">{contentTitle}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-[#F4F2EC] rounded-lg">
              <X className="w-5 h-5 text-[#475569]" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {sent ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-[#0F1A2A] mb-2">Email Sent!</h3>
              <p className="text-[#475569] text-sm mb-4">
                {name ? `${name} will` : 'They\'ll'} receive the study at {email}
              </p>
              {shareUrl && (
                <div className="p-3 bg-[#F9F8F6] rounded-lg text-sm">
                  <p className="text-[#475569] mb-1">Share link:</p>
                  <a href={shareUrl} className="text-[#1B3A6B] break-all hover:underline">{shareUrl}</a>
                </div>
              )}
              <button
                onClick={onClose}
                className="mt-6 btn-primary px-6 py-2.5 rounded-lg font-medium"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg flex items-center gap-2 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Recipient Email *</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]"
                    placeholder="friend@example.com"
                    data-testid="email-recipient-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Recipient Name (optional)</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]"
                    placeholder="John"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Subject (optional)</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]"
                    placeholder="Check out this Bible study!"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Personal Message (optional)</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] resize-none"
                    rows={3}
                    placeholder="I thought you might enjoy this study..."
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={onClose}
                  className="flex-1 btn-secondary py-3 rounded-lg font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending}
                  className="flex-1 btn-primary py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="send-email-button"
                >
                  {sending ? (
                    <><Loader2 className="w-5 h-5 animate-spin" />Sending...</>
                  ) : (
                    <><Send className="w-5 h-5" />Send Email</>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
